import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { AppHeader } from "@/components/AppHeader";
import { MobileDrawer } from "@/components/MobileDrawer";
import { MobileNavigation } from "@/components/MobileNavigation";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AuthProvider } from "@/contexts/AuthContext";
import { SecurityProvider } from "@/contexts/SecurityContext";
import { SmartNotifications } from "@/components/notifications/SmartNotifications";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { LocationTracker } from "@/components/LocationTracker";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileSplashScreen } from "@/components/MobileSplashScreen";
import { AuthDebugInfo } from "@/components/auth/AuthDebugInfo";
import { useSessionKeepAlive } from "@/hooks/useSessionKeepAlive";
import { SecurityHeaders } from "@/components/security/SecurityHeaders";
import { cleanupStoredTokens } from "@/utils/mapbox";

// Import all pages
import Index from "../pages/Index";
import Login from "../pages/Login";
import Clients from "../pages/Clients";
import { ClientKanban } from "../pages/ClientKanban";
import ClientStatus from "../pages/ClientStatus";
import ClientStatusManagement from "../pages/ClientStatusManagement";
import Companies from "../pages/Companies";
import EmployeeDashboard from "../pages/EmployeeDashboard";
import Employees from "../pages/Employees";
import Assets from "../pages/Assets";
import AssetCategories from "../pages/AssetCategories";
import EmployeeAssets from "../pages/EmployeeAssets";
import Tasks from "../pages/Tasks";
import CreatePackage from "../pages/CreatePackage";
import PackageWizard from "../pages/PackageWizard";
import TourismPackages from "../pages/TourismPackages";
import PackageDetails from "../pages/PackageDetails";
import PackageResults from "../pages/PackageResults";
import Hotels from "../pages/Hotels";
import Suppliers from "../pages/Suppliers";
import Visas from "../pages/Visas";
import Messages from "../pages/Messages";
import Notifications from "../pages/Notifications";
import Settings from "../pages/Settings";
import EmployeeLoginMap from "../pages/EmployeeLoginMap";
import ActivityLogs from "../pages/ActivityLogs";
import SessionLogs from "../pages/SessionLogs";
import Permissions from "../pages/Permissions";
import RoleManagement from "../pages/RoleManagement";
import SecurityMonitoring from "../pages/SecurityMonitoring";
import ChatwootClients from "../pages/ChatwootClients";
import Teletel from "../pages/Teletel";
import PdfTemplateSettingsPage from "../pages/PdfTemplateSettingsPage";
import PackageInclusions from "../pages/PackageInclusions";
import PackageExclusions from "../pages/PackageExclusions";
import EmployeeProfile from "../pages/EmployeeProfile";
import MyProfile from "../pages/MyProfile";
import PackageSearch from "../pages/PackageSearch";
import Services from "../pages/Services";
import ServiceCountryMapping from "../pages/ServiceCountryMapping";
import Countries from "../pages/Countries";
import Cities from "../pages/Cities";
import Airlines from "../pages/Airlines";
import Airports from "../pages/Airports";
import Flights from "../pages/Flights";
import Ports from "../pages/Ports";
import ShippingCompanies from "../pages/ShippingCompanies";
import VehicleTypes from "../pages/VehicleTypes";
import CountryVehicleMapping from "../pages/CountryVehicleMapping";
import AirportTransfers from "../pages/AirportTransfers";
import TouristTours from "../pages/TouristTours";
import TurkishNorthTransport from "../pages/TurkishNorthTransport";
import Drivers from "../pages/Drivers";
import VisaTypes from "../pages/VisaTypes";
import SalesTrendsAnalysis from "../pages/SalesTrendsAnalysis";
import AIAnalytics from "../pages/AIAnalytics";
import SmartPerformanceMonitoring from "../pages/SmartPerformanceMonitoring";
import EmployeeAnalytics from "../pages/EmployeeAnalytics";
import CustomerReceptionBoards from "../pages/CustomerReceptionBoards";
import EmailSystem from "../pages/EmailSystem";
import SystemHealthDashboard from "../pages/SystemHealthDashboard";
import SystemMonitoring from "../pages/SystemMonitoring";
import AdminAnnouncementsPage from "../pages/AdminAnnouncements";
import SentryMonitoring from "../pages/monitoring/SentryMonitoring";
import LogRocketMonitoring from "../pages/monitoring/LogRocketMonitoring";
import MixpanelMonitoring from "../pages/monitoring/MixpanelMonitoring";
import JestTesting from "../pages/monitoring/JestTesting";
import NewRelicMonitoring from "../pages/monitoring/NewRelicMonitoring";
import DataDogMonitoring from "../pages/monitoring/DataDogMonitoring";
import CypressTesting from "../pages/monitoring/CypressTesting";
import StorybookMonitoring from "../pages/monitoring/StorybookMonitoring";
import LighthouseMonitoring from "../pages/monitoring/LighthouseMonitoring";

import ClientSelfRegistration from "../pages/ClientSelfRegistration";
import ClientProfile from "../pages/ClientProfile";
import AddClient from "../pages/AddClient";
import PublicBookingSystem from "../pages/PublicBookingSystem";
import CompanyManagement from "../pages/CompanyManagement";
import ClientsManagement from "../pages/ClientsManagement";
import DirectClients from "../pages/DirectClients";
import CorporateClients from "../pages/CorporateClients";
import B2BClients from "../pages/B2BClients";
import BookingCenter from "../pages/booking/BookingCenter";
import WaitlistManagement from "../pages/booking/WaitlistManagement";
import BookingModificationsPage from '@/pages/booking/BookingModificationsPage';
import MessageTemplatesPage from '@/pages/booking/MessageTemplatesPage';
import ComplaintManagementPage from '@/pages/quality/ComplaintManagementPage';
import ServiceQualityPage from '@/pages/quality/ServiceQualityPage';
import LegalCompliancePage from '@/pages/quality/LegalCompliancePage';
import DocumentArchivePage from '@/pages/quality/DocumentArchivePage';
import ProcessAuditPage from '@/pages/quality/ProcessAuditPage';
import Pricing from '@/pages/Pricing';
import TenantSignup from '@/pages/TenantSignup';
import SuperAdmin from '@/pages/SuperAdmin';
import ClientFollowUpManagement from '@/pages/ClientFollowUpManagement';
import SmartReminders from '@/pages/SmartReminders';
import FollowUpReports from '@/pages/FollowUpReports';
import TaskAnalytics from '@/pages/TaskAnalytics';
import TimeTrackingReports from '@/pages/TimeTrackingReports';
import EmployeePerformanceDashboard from '@/pages/EmployeePerformanceDashboard';
import TeamPerformanceComparison from '@/pages/TeamPerformanceComparison';
import PerformanceGoalsManagement from '@/pages/PerformanceGoalsManagement';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ResponsiveLayout({ children }: { children: React.ReactNode }) {
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // Keep session alive
  useSessionKeepAlive();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background" dir="rtl">
        {/* Desktop Sidebar - Hidden on mobile, shown on lg+ */}
        <AppSidebar />
        
        {/* Mobile Drawer - Only on mobile */}
        <MobileDrawer />
        
        <div className="flex-1 flex flex-col min-h-0 w-full">
          <AppHeader />
          
          {/* Main Content with responsive padding */}
          <main className={`
            flex-1 overflow-auto bg-gradient-to-br from-background via-muted/30 to-accent/20
            ${isMobile ? 'p-2 pb-20 sm:p-3 sm:pb-20' : 'p-4 md:p-5 lg:p-6 xl:p-8'}
            transition-all duration-300
          `}>
            {children}
          </main>
          
          {/* Mobile Bottom Navigation - Only on mobile */}
          <MobileNavigation onMenuClick={() => setIsMobileDrawerOpen(true)} />
          
          {/* Smart Notifications - adjusted for mobile */}
          <SmartNotifications 
            position={isMobile ? "bottom-right" : "top-right"} 
            maxNotifications={isMobile ? 3 : 5} 
          />
          
          {/* Location Tracker */}
          <LocationTracker />
          
          {/* Auth Debug Info - Development Only */}
          <AuthDebugInfo />
        </div>
      </div>
    </SidebarProvider>
  );
}

export function EnhancedApp() {
  // Clean up any stored API tokens on app initialization
  useEffect(() => {
    cleanupStoredTokens();
  }, []);

  return (
    <ErrorBoundary>
      <SecurityProvider>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <SecurityHeaders />
            <MobileSplashScreen />
            <Toaster />
            <BrowserRouter>
              <Routes>
                {/* Public routes - الصفحات الوحيدة المتاحة للزوار */}
                <Route path="/login" element={<Login />} />
                <Route path="/client-registration" element={<ClientSelfRegistration />} />
                <Route path="/public-booking" element={<PublicBookingSystem />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/tenant-signup" element={<TenantSignup />} />
                
                {/* Default route - محمي بـ AuthGuard */}
                <Route path="/" element={
                  <AuthGuard>
                    <Navigate to="/dashboard" replace />
                  </AuthGuard>
                } />
                
                {/* Protected routes */}
                <Route
                  path="/*"
                  element={
                    <AuthGuard>
                      <ResponsiveLayout>
                        <Routes>
                          <Route path="/dashboard" element={<Index />} />
                          <Route path="/clients" element={<Clients />} />
                           <Route path="/clients/add" element={<AddClient />} />
                           <Route path="/clients/direct" element={<DirectClients />} />
                           <Route path="/clients/corporate" element={<CorporateClients />} />
                           <Route path="/clients/b2b" element={<B2BClients />} />
                           <Route path="/client-follow-up" element={<ClientKanban />} />
                           <Route path="/client-status" element={<ClientStatus />} />
                           <Route path="/client-status-management" element={<ClientStatusManagement />} />
                           <Route path="/companies" element={<Companies />} />
                            <Route path="/company-management" element={<CompanyManagement />} />
                            <Route path="/chatwoot-clients" element={<ChatwootClients />} />
                            <Route path="/employees" element={<Employees />} />
                            <Route path="/assets" element={<Assets />} />
                            <Route path="/asset-categories" element={<AssetCategories />} />
                            <Route path="/employee-assets" element={<EmployeeAssets />} />
                           <Route path="/employee-profile" element={<EmployeeProfile />} />
                           <Route path="/my-profile" element={<MyProfile />} />
                           <Route path="/employee-analytics" element={<EmployeeAnalytics />} />
                           <Route path="/tasks" element={<Tasks />} />
                           <Route path="/package-wizard" element={<PackageWizard />} />
                           <Route path="/create-package" element={<CreatePackage />} />
                           <Route path="/tourism-packages" element={<TourismPackages />} />
                           <Route path="/package-search" element={<PackageSearch />} />
                           <Route path="/package-details/:id" element={<PackageDetails />} />
                           <Route path="/package-results" element={<PackageResults />} />
                           <Route path="/hotels" element={<Hotels />} />
                           <Route path="/suppliers" element={<Suppliers />} />
                           <Route path="/visas" element={<Visas />} />
                           <Route path="/visa-types" element={<VisaTypes />} />
                           <Route path="/messages" element={<Messages />} />
                           <Route path="/notifications" element={<Notifications />} />
                           <Route path="/settings" element={<Settings />} />
                           <Route path="/employee-login-map" element={<EmployeeLoginMap />} />
                           <Route path="/activity-logs" element={<ActivityLogs />} />
                           <Route path="/session-logs" element={<SessionLogs />} />
                           <Route path="/permissions" element={<Permissions />} />
                           <Route path="/role-management" element={<RoleManagement />} />
                           <Route path="/security-monitoring" element={<SecurityMonitoring />} />
                           <Route path="/pdf-template-settings" element={<PdfTemplateSettingsPage />} />
                           <Route path="/package-inclusions" element={<PackageInclusions />} />
                           <Route path="/package-exclusions" element={<PackageExclusions />} />
                           <Route path="/services" element={<Services />} />
                           <Route path="/service-country-mapping" element={<ServiceCountryMapping />} />
                           <Route path="/countries" element={<Countries />} />
                           <Route path="/cities" element={<Cities />} />
                           <Route path="/airlines" element={<Airlines />} />
                           <Route path="/airports" element={<Airports />} />
                           <Route path="/flights" element={<Flights />} />
                           <Route path="/ports" element={<Ports />} />
                           <Route path="/shipping-companies" element={<ShippingCompanies />} />
                           <Route path="/vehicle-types" element={<VehicleTypes />} />
                           <Route path="/country-vehicle-mapping" element={<CountryVehicleMapping />} />
                           <Route path="/airport-transfers" element={<AirportTransfers />} />
                           <Route path="/tourist-tours" element={<TouristTours />} />
                           <Route path="/turkish-north-transport" element={<TurkishNorthTransport />} />
                           <Route path="/drivers" element={<Drivers />} />
                           <Route path="/sales-trends" element={<SalesTrendsAnalysis />} />
                           <Route path="/ai-analytics" element={<AIAnalytics />} />
                            <Route path="/smart-performance" element={<SmartPerformanceMonitoring />} />
                            <Route path="/customer-reception-boards" element={<CustomerReceptionBoards />} />
                            <Route path="/system-health" element={<SystemHealthDashboard />} />
                            <Route path="/system-monitoring" element={<SystemMonitoring />} />
             <Route path="/admin-announcements" element={<AdminAnnouncementsPage />} />
             <Route path="/booking-center" element={<BookingCenter />} />
             <Route path="/waitlist-management" element={<WaitlistManagement />} />
            <Route path="/booking-modifications" element={<BookingModificationsPage />} />
            <Route path="/message-templates" element={<MessageTemplatesPage />} />
            <Route path="/complaint-management" element={<ComplaintManagementPage />} />
            <Route path="/service-quality" element={<ServiceQualityPage />} />
            <Route path="/legal-compliance" element={<LegalCompliancePage />} />
            <Route path="/document-archive" element={<DocumentArchivePage />} />
            <Route path="/process-audit" element={<ProcessAuditPage />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/client-follow-up-management" element={<ClientFollowUpManagement />} />
            <Route path="/smart-reminders-page" element={<SmartReminders />} />
            <Route path="/followup-reports" element={<FollowUpReports />} />
            <Route path="/task-analytics" element={<TaskAnalytics />} />
          <Route path="/time-tracking-reports" element={<TimeTrackingReports />} />
          <Route path="/employee-performance" element={<EmployeePerformanceDashboard />} />
          <Route path="/team-performance" element={<TeamPerformanceComparison />} />
          <Route path="/performance-goals" element={<PerformanceGoalsManagement />} />
        </Routes>
                      </ResponsiveLayout>
                    </AuthGuard>
                  }
                />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </AuthProvider>
    </SecurityProvider>
    </ErrorBoundary>
  );
}